import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash, Stack } from "@phosphor-icons/react";

export default function Tariffs() {
  const [tariffs, setTariffs] = useState([]);

  const load = async () => {
    const { data } = await api.get("/tariffs");
    // Ensure tiers is always an array; enable toggle if backend already has tiers
    setTariffs(data.map(t => {
      const tiers = Array.isArray(t.tiers) ? t.tiers : [];
      return { ...t, tiers, tiers_enabled: tiers.length > 0 };
    }));
  };
  useEffect(() => { load(); }, []);

  const save = async (t) => {
    try {
      const payload = {
        free_minutes: Number(t.free_minutes),
        hourly_rate: Number(t.hourly_rate),
        daily_max: Number(t.daily_max),
        grace_period_minutes: Number(t.grace_period_minutes || 5),
      };
      // Only include tiers if the user enabled them
      if (t.tiers_enabled && Array.isArray(t.tiers) && t.tiers.length > 0) {
        payload.tiers = t.tiers.map(x => ({
          from_hour: Number(x.from_hour),
          to_hour: x.to_hour === null || x.to_hour === "" ? null : Number(x.to_hour),
          rate: Number(x.rate),
        }));
      } else {
        payload.tiers = null;
      }
      await api.put(`/tariffs/${t.parking_lot_key}`, payload);
      toast.success(`${t.parking_lot_key === "ust_kat" ? "Üst Kat" : "Alt Kat"} tarifesi güncellendi. Aktif oturumlar bir sonraki hesaplama döngüsünde yeni fiyata geçer.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    }
  };

  const update = (idx, field, value) => {
    const c = [...tariffs];
    c[idx] = { ...c[idx], [field]: value };
    // Kademeli tarife toggle'ı AÇILINCA ve henüz hiç kademe yoksa,
    // varsayılan olarak 2 kademe otomatik oluşturulur:
    //   0-1 saat = mevcut sabit saatlik ücret
    //   1-∞      = sabit saatlik + 50 (kullanıcının en sık istediği model)
    // Bu sayede toggle direkt anlamlı olur ve save'de tiers boş gitmez,
    // sayfa yenilendiğinde toggle kapalı görünmez.
    if (field === "tiers_enabled" && value && (!c[idx].tiers || c[idx].tiers.length === 0)) {
      const base = Number(c[idx].hourly_rate || 100);
      c[idx].tiers = [
        { from_hour: 0, to_hour: 1, rate: base },
        { from_hour: 1, to_hour: null, rate: base + 50 },
      ];
    }
    setTariffs(c);
  };

  const updateTier = (tIdx, tierIdx, field, value) => {
    const c = [...tariffs];
    const tiers = [...(c[tIdx].tiers || [])];
    tiers[tierIdx] = { ...tiers[tierIdx], [field]: value };
    c[tIdx] = { ...c[tIdx], tiers };
    setTariffs(c);
  };

  const addTier = (tIdx) => {
    const c = [...tariffs];
    const tiers = [...(c[tIdx].tiers || [])];
    const lastTo = tiers.length > 0 ? Number(tiers[tiers.length - 1].to_hour || tiers.length) : 0;
    tiers.push({ from_hour: lastTo, to_hour: lastTo + 1, rate: c[tIdx].hourly_rate || 100 });
    c[tIdx] = { ...c[tIdx], tiers, tiers_enabled: true };
    setTariffs(c);
  };

  const removeTier = (tIdx, tierIdx) => {
    const c = [...tariffs];
    const tiers = [...(c[tIdx].tiers || [])];
    tiers.splice(tierIdx, 1);
    c[tIdx] = { ...c[tIdx], tiers };
    setTariffs(c);
  };

  /**
   * Hızlı Ayar Uygula: "0-1 saat X TL, sonrası saat başı +Y TL"
   * modelini tek tıkla 6 kademeye çevirir (2. saatten 7. saate kadar).
   * Kullanıcı sonrasında istediği kademeyi silebilir veya değiştirebilir.
   */
  const applyQuick = (tIdx) => {
    const c = [...tariffs];
    const t = c[tIdx];
    const first = Number(t._quick_first ?? t.hourly_rate ?? 150);
    const inc = Number(t._quick_inc ?? 50);
    const tiers = [{ from_hour: 0, to_hour: 1, rate: first }];
    // 1-2, 2-3, ..., 6-∞
    for (let h = 1; h <= 5; h++) {
      tiers.push({
        from_hour: h,
        to_hour: h === 5 ? null : h + 1,
        rate: first + inc * h,
      });
    }
    c[tIdx] = { ...t, tiers, tiers_enabled: true };
    setTariffs(c);
    toast.success(`Kademeler dolduruldu: 0-1s ${first}₺, sonrası saat başı +${inc}₺`);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Fiyatlandırma</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Tarife Yöneticisi</h1>
        <div className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Tarifeleri güncellediğinizde <b>aktif otoparklardaki anlık ücretler otomatik güncellenir</b>.
          Kademeli (artan) saatlik ücret için &quot;Kademeli Tarife&quot; özelliğini aktifleştirin.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {tariffs.map((t, i) => (
          <Card key={t.id} className="rounded-sm border-border">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold">
                  {t.parking_lot_key === "ust_kat" ? "Üst Kat" : "Alt Kat"}
                </h3>
                <span className="text-xs uppercase tracking-widest text-muted-foreground">Tarife</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Ücretsiz dakika</Label>
                  <Input data-testid={`tariff-${t.parking_lot_key}-free`} type="number" value={t.free_minutes} onChange={(e) => update(i, "free_minutes", e.target.value)} className="rounded-sm" />
                </div>
                <div>
                  <Label>Sabit saatlik ücret (₺)</Label>
                  <Input data-testid={`tariff-${t.parking_lot_key}-hourly`} type="number" step="0.01" value={t.hourly_rate} onChange={(e) => update(i, "hourly_rate", e.target.value)} className="rounded-sm" />
                </div>
                <div>
                  <Label>Günlük azami (₺)</Label>
                  <Input data-testid={`tariff-${t.parking_lot_key}-daily`} type="number" step="0.01" value={t.daily_max} onChange={(e) => update(i, "daily_max", e.target.value)} className="rounded-sm" />
                  <p className="text-xs text-muted-foreground mt-1" data-testid={`tariff-${t.parking_lot_key}-daily-hint`}>
                    24 saati aşan kalışlarda her tam gün bu tutar; artan saatler kademeye göre üzerine eklenir.
                  </p>
                </div>
                <div>
                  <Label>Grace (dk) — ücretsiz çıkış toleransı</Label>
                  <Input
                    data-testid={`tariff-${t.parking_lot_key}-grace`}
                    type="number" min="0" max="60"
                    value={t.grace_period_minutes ?? 5}
                    onChange={(e) => update(i, "grace_period_minutes", e.target.value)}
                    className="rounded-sm"
                  />
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Girişten X dk içinde çıkan araçlar muaf (yanlış giriş koruması). 0 = kapalı.
                  </div>
                </div>
              </div>

              {/* Progressive tiered pricing */}
              <div className="border-t border-border pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Stack size={18} className="text-primary" />
                    <span className="font-semibold text-sm uppercase tracking-widest">Kademeli Tarife</span>
                  </div>
                  <Switch
                    checked={!!(t.tiers_enabled || (t.tiers && t.tiers.length > 0))}
                    onCheckedChange={(v) => update(i, "tiers_enabled", v)}
                    data-testid={`tariff-${t.parking_lot_key}-tiers-toggle`}
                  />
                </div>
                <div className="text-xs text-muted-foreground">
                  <b>Her satır = tam bir süre dilimi ve o dilimin TOPLAM ücreti.</b>{" "}
                  Örneğin 3 dilim kurarsan: 0-1 saat = 150₺, 1-2 saat = 200₺,
                  2. saat üzeri = 250₺. Müşteri 2 saat 38 dk kalmışsa
                  <b> yalnız 250₺ ödenir</b> (toplama yok). Kademeler otomatik
                  büyüyerek olduğundan fazla saklandığı için müşteri hiçbir zaman
                  bir üst dilimden fazla ödemez.
                </div>
                {(t.tiers_enabled || (t.tiers && t.tiers.length > 0)) && (
                  <>
                    {/* Hızlı Ayar: 0-1 saat X, sonrası saat başı +Y otomatik doldur */}
                    <div className="flex items-center gap-2 flex-wrap bg-primary/5 border border-primary/20 rounded-sm p-2">
                      <span className="text-[10px] uppercase tracking-widest font-semibold text-primary">Hızlı Ayar</span>
                      <span className="text-xs text-muted-foreground">0-1 saat</span>
                      <Input type="number" step="0.01"
                             defaultValue={t.hourly_rate}
                             onBlur={(e) => update(i, "_quick_first", e.target.value)}
                             className="rounded-sm h-8 w-20 font-mono text-xs"
                             data-testid={`tariff-${t.parking_lot_key}-quick-first`}
                             placeholder="150" />
                      <span className="text-xs text-muted-foreground">TL, sonrası saat başı +</span>
                      <Input type="number" step="0.01"
                             defaultValue={50}
                             onBlur={(e) => update(i, "_quick_inc", e.target.value)}
                             className="rounded-sm h-8 w-20 font-mono text-xs"
                             data-testid={`tariff-${t.parking_lot_key}-quick-inc`}
                             placeholder="50" />
                      <span className="text-xs text-muted-foreground">TL</span>
                      <Button size="sm" variant="outline"
                              onClick={() => applyQuick(i)}
                              data-testid={`tariff-${t.parking_lot_key}-quick-apply`}
                              className="rounded-sm h-8 text-xs uppercase tracking-widest font-semibold">
                        Uygula
                      </Button>
                    </div>
                    <div className="space-y-2" data-testid={`tariff-${t.parking_lot_key}-tiers-list`}>
                    {(t.tiers || []).map((tier, tierIdx) => (
                      <div key={tierIdx} className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-3">
                          <Label className="text-[10px] uppercase">Başlangıç (saat)</Label>
                          <Input type="number" step="0.5" min="0" value={tier.from_hour}
                                 onChange={(e) => updateTier(i, tierIdx, "from_hour", e.target.value)}
                                 className="rounded-sm h-9 font-mono"
                                 data-testid={`tariff-${t.parking_lot_key}-tier-${tierIdx}-from`} />
                        </div>
                        <div className="col-span-3">
                          <Label className="text-[10px] uppercase">Bitiş (saat)</Label>
                          <Input type="number" step="0.5" min="0"
                                 value={tier.to_hour === null ? "" : tier.to_hour}
                                 placeholder="∞ (sonsuz)"
                                 onChange={(e) => updateTier(i, tierIdx, "to_hour", e.target.value)}
                                 className="rounded-sm h-9 font-mono"
                                 data-testid={`tariff-${t.parking_lot_key}-tier-${tierIdx}-to`} />
                        </div>
                        <div className="col-span-4">
                          <Label className="text-[10px] uppercase">Ücret ₺/saat</Label>
                          <Input type="number" step="0.01" value={tier.rate}
                                 onChange={(e) => updateTier(i, tierIdx, "rate", e.target.value)}
                                 className="rounded-sm h-9 font-mono font-bold text-primary"
                                 data-testid={`tariff-${t.parking_lot_key}-tier-${tierIdx}-rate`} />
                        </div>
                        <div className="col-span-2">
                          <Button size="sm" variant="ghost"
                                  onClick={() => removeTier(i, tierIdx)}
                                  data-testid={`tariff-${t.parking_lot_key}-tier-${tierIdx}-remove`}
                                  className="rounded-sm text-destructive w-full h-9">
                            <Trash size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <Button size="sm" variant="outline" onClick={() => addTier(i)}
                            data-testid={`tariff-${t.parking_lot_key}-add-tier`}
                            className="rounded-sm w-full uppercase tracking-widest text-xs">
                      <Plus size={12} /> Kademe Ekle
                    </Button>
                  </div>
                  </>
                )}
              </div>

              <Button data-testid={`tariff-save-${t.parking_lot_key}`} onClick={() => save(t)} className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold">
                Kaydet
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
